import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { t } from "@wr/i18n";
import { AuthCard, Button, CheckIcon, CountdownAction, TextField } from "@wr/ui";
import { requestPasswordReset } from "../lib/auth";
import { emailLooksValid } from "../components/CredentialsFields";
import { WEB_ORIGIN } from "../lib/origin";

/**
 * Forgot password — design spec §8.1.
 *
 * **LOCKED — identical success regardless of whether the email exists.** Same
 * copy, same timing, same panel. There is deliberately no error branch in this
 * component that a caller could use to tell a real address from a fake one:
 * `send()` resolves into the confirmation state on *every* non-transport
 * outcome, because branching on the server's answer is precisely the leak PRD
 * §4.3 forbids.
 *
 * **LOCKED — "Back to login" is visible in every state** of this screen and of
 * `/reset`.
 */
export function ForgotScreen() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendKey, setResendKey] = useState(0);

  const invalid = touched && email.length > 0 && !emailLooksValid(email);

  async function send() {
    setSending(true);
    try {
      await requestPasswordReset(email, `${WEB_ORIGIN}/reset`);
    } catch {
      // Swallowed on purpose. A rate limit, an unknown address and a real send
      // must all look the same from here — see the LOCKED note above. A genuine
      // transport failure is the one thing this hides, and the cost of that is
      // one confused player versus an enumeration oracle for everyone.
    } finally {
      setSending(false);
      setSent(true);
      setResendKey((value) => value + 1);
    }
  }

  return (
    <div className="wr-auth-layout">
      <div className="wr-auth-layout__form">
        <div className="wr-form-column">
          <AuthCard testId="forgot-card">
            {sent ? (
              // The success state REPLACES the form entirely (§8.1).
              <div
                className="wr-stack"
                style={{ gap: 16, alignItems: "center", textAlign: "center" }}
                data-testid="forgot-sent"
              >
                <span
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "var(--wr-r-pill)",
                    background: "var(--wr-good)",
                    border: "var(--wr-stroke)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-hidden="true"
                >
                  <CheckIcon size={24} />
                </span>
                <h1 className="wr-display-md" data-route-heading>
                  {t("forgot.sentTitle")}
                </h1>
                <p className="wr-body-md wr-ink-70">{t("forgot.sent")}</p>
                <CountdownAction
                  key={resendKey}
                  seconds={60}
                  size="md"
                  variant="secondary"
                  fullWidth
                  label={t("forgot.resend")}
                  waitingLabel={() => t("forgot.resend")}
                  onAction={() => void send()}
                  testId="forgot-resend"
                />
              </div>
            ) : (
              <form
                className="wr-stack"
                style={{ gap: 16 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (emailLooksValid(email) && !sending) void send();
                }}
              >
                <h1 className="wr-display-lg" data-route-heading>
                  {t("forgot.title")}
                </h1>

                <TextField
                  id="forgot-email"
                  eyebrow={t("auth.email")}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  readOnly={sending}
                  data-testid="forgot-email"
                  invalid={invalid}
                  helper={invalid ? t("auth.emailInvalid") : undefined}
                  onBlur={() => setTouched(true)}
                  onChange={(event) => setEmail(event.target.value)}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={sending}
                  loadingLabel={t("common.working")}
                  disabled={!emailLooksValid(email)}
                  data-testid="forgot-submit"
                >
                  {t("forgot.submit")}
                </Button>
              </form>
            )}

            {/* Persistent in EVERY state (§8, LOCKED). */}
            <Link
              className="wr-btn wr-btn--ghost wr-btn--md wr-btn--full"
              to="/login"
              data-testid="forgot-back"
            >
              {t("common.backToLogin")}
            </Link>
          </AuthCard>
        </div>
      </div>
    </div>
  );
}
